/**
 * Wraps a pull handler so the checkpoint survives IndexedDB schema
 * migrations and page refreshes. RxDB stores checkpoints internally
 * in a meta table that can be lost during collection version changes
 * (migration) or database re-initialization. This wrapper keeps the
 * last-known-good checkpoint in localStorage as a fallback.
 *
 * Usage:
 * ```ts
 * pull: {
 *   batchSize: 100,
 *   handler: withCheckpointFallback('workspaces-trpc', async (checkpoint, batchSize) => { ... }),
 * }
 * ```
 */

import { type Checkpoint } from '../../../shared/sync/schemas';

const STORAGE_PREFIX = 'rxdb-checkpoint-';
const knownReplicationIds = ['processes-trpc', 'workspaces-trpc'] as const;

type PullHandler<Doc> = (
  checkpoint: Checkpoint | undefined,
  batchSize: number,
) => Promise<PullResult<Doc>>;

type PullResult<Doc> = {
  checkpoint: Checkpoint | undefined;
  documents: Doc[];
};

export const checkpointFallbackId = (userId: string, replicationId: string) =>
  `${userId}:${replicationId}`;

export const clearCheckpointFallbacks = (userId: string): void => {
  try {
    const userScopedPrefix = `${STORAGE_PREFIX}${userId}:`;
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(userScopedPrefix)) {
        localStorage.removeItem(key);
      }
    }

    for (const replicationId of knownReplicationIds) {
      localStorage.removeItem(
        `${STORAGE_PREFIX}${checkpointFallbackId(userId, replicationId)}`,
      );
      localStorage.removeItem(`${STORAGE_PREFIX}${replicationId}`);
    }
  } catch {
    // localStorage may be unavailable; non-critical.
  }
};

const readStored = (id: string): Checkpoint | null => {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${id}`);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as Record<string, unknown>).id === 'string' &&
      typeof (parsed as Record<string, unknown>).updatedAt === 'string'
    ) {
      return parsed as Checkpoint;
    }

    return null;
  } catch {
    return null;
  }
};

const writeStored = (id: string, cp: Checkpoint): void => {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${id}`, JSON.stringify(cp));
  } catch {
    // localStorage may be full or unavailable; non-critical.
  }
};

export const withCheckpointFallback =
  <Doc>(replicationId: string, handler: PullHandler<Doc>): PullHandler<Doc> =>
  async (checkpoint, batchSize) => {
    // When RxDB has no internal checkpoint (e.g. after schema migration),
    // fall back to localStorage which survives IndexedDB resets.
    const effective = checkpoint ?? readStored(replicationId) ?? undefined;
    const result = await handler(effective, batchSize);

    // Persist the returned checkpoint so the next page load can use it.
    if (result.checkpoint) {
      writeStored(replicationId, result.checkpoint);
    }

    return result;
  };
